import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  DestroyRef,
  HostListener,
  Input,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { Route, Router, RouterLink } from '@angular/router';
import { TimeUtils, Timer } from '@freelancer/time-utils';

/**
 * Prefetches lazy-loaded route bundles on hover/focus so that navigation
 * feels instant. Place on any element that also carries a `[routerLink]`.
 *
 * The directive resolves the target URL against `Router.config`, walks the
 * route tree to find every lazy boundary (`loadChildren` / `loadComponent`)
 * along the path — including through redirects and nested child routes —
 * and triggers the corresponding dynamic `import()` calls.
 *
 * Each URL is only prefetched once; failed loads clear the cache so a
 * retry is possible on the next hover.
 *
 * @example
 * ```html
 * <a [routerLink]="['/projects', project.id]" flPrefetchRoute>
 *   {{ project.title }}
 * </a>
 *
 * <!-- Dynamically toggled -->
 * <a [routerLink]="['/search']" [flPrefetchRoute]="shouldPrefetch">
 *   Search
 * </a>
 * ```
 */
@Directive({
  selector: '[flPrefetchRoute]',
  standalone: true,
})
export class PrefetchRouteDirective {
  private static readonly prefetchedUrls = new Set<string>();

  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly timeUtils = inject(TimeUtils);
  private readonly routerLink = inject(RouterLink, {
    optional: true,
    self: true,
  });

  /** Pass `false` to disable prefetching. */
  @Input() flPrefetchRoute: boolean | '' = true;

  private debounceTimer$: Timer | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  @HostListener('mouseenter')
  @HostListener('focusin')
  onPointerEnter(): void {
    if (
      this.flPrefetchRoute === false ||
      !isPlatformBrowser(this.platformId) ||
      !this.routerLink
    ) {
      return;
    }

    this.clearTimer();
    this.debounceTimer$ = this.timeUtils.setTimeout(() => this.prefetch(), 100);
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  onPointerLeave(): void {
    this.clearTimer();
  }

  private prefetch(): void {
    const urlTree = this.routerLink?.urlTree;
    if (!urlTree) {
      return;
    }

    const url = this.router.serializeUrl(urlTree);
    if (PrefetchRouteDirective.prefetchedUrls.has(url)) {
      return;
    }
    PrefetchRouteDirective.prefetchedUrls.add(url);

    const segments = url.split('/').filter(s => s.length > 0);
    this.loadLazyBoundaries(this.router.config, segments, 0);
  }

  /**
   * Walks the route config tree, matching URL segments against route paths.
   * When a lazy boundary is found (`loadChildren` or `loadComponent`), the
   * corresponding bundle is fetched. For `loadChildren`, the walk continues
   * into the resolved child routes to pick up deeper lazy boundaries.
   */
  private loadLazyBoundaries(
    routes: readonly Route[],
    segments: string[],
    index: number,
  ): void {
    for (const route of this.findActionableMatches(routes, segments, index)) {
      const nextIndex = index + this.segmentsOf(route).length;

      if (route.redirectTo && typeof route.redirectTo === 'string') {
        this.followRedirect(route, routes, segments, index);
        return;
      }

      if (route.loadChildren) {
        this.loadModuleAndContinue(route.loadChildren(), segments, nextIndex);
        return;
      }

      if (route.loadComponent) {
        this.loadBundle(route.loadComponent());
        if (route.children) {
          this.loadLazyBoundaries(route.children, segments, nextIndex);
        }
        return;
      }

      if (route.children) {
        this.loadLazyBoundaries(route.children, segments, nextIndex);
        return;
      }
    }
  }

  /**
   * Returns routes that both match the current URL position and have
   * something useful to do (redirect, lazy-load, or recurse into children).
   */
  private findActionableMatches(
    routes: readonly Route[],
    segments: string[],
    index: number,
  ): Route[] {
    return routes.filter(route => {
      const pathSegments = this.segmentsOf(route);
      if (!this.pathMatchesUrl(pathSegments, segments, index)) {
        return false;
      }
      const nextIndex = index + pathSegments.length;
      return this.hasAction(route, pathSegments, nextIndex, segments.length);
    });
  }

  private segmentsOf(route: Route): string[] {
    return (route.path ?? '').split('/').filter(s => s.length > 0);
  }

  /**
   * Checks whether a route's path segments match the URL at the given
   * position. Empty paths always match. Dynamic (`:id`) and wildcard
   * (`**`) segments match any value.
   */
  private pathMatchesUrl(
    pathSegments: readonly string[],
    urlSegments: readonly string[],
    startIndex: number,
  ): boolean {
    if (pathSegments.length === 0) {
      return true;
    }

    return pathSegments.every((segment, i) => {
      const urlIndex = startIndex + i;
      if (urlIndex >= urlSegments.length) {
        return false;
      }
      if (segment.startsWith(':') || segment === '**') {
        return true;
      }
      return segment === urlSegments[urlIndex];
    });
  }

  /**
   * A matched route is "actionable" when it has something to prefetch or
   * recurse into. Two cases are filtered out:
   * - Redirects when there are still URL segments to consume (Angular's
   *   `pathMatch: 'full'` semantics).
   * - Empty-path routes with no lazy loader, redirect, or children.
   */
  private hasAction(
    route: Route,
    pathSegments: string[],
    nextIndex: number,
    totalSegments: number,
  ): boolean {
    if (route.redirectTo && nextIndex < totalSegments) {
      return false;
    }

    if (pathSegments.length === 0) {
      return !!(
        route.redirectTo ||
        route.loadChildren ||
        route.loadComponent ||
        route.children
      );
    }

    return true;
  }

  private followRedirect(
    route: Route,
    allRoutes: readonly Route[],
    segments: string[],
    index: number,
  ): void {
    const target = route.redirectTo as string;
    const redirectSegments = target.split('/').filter(s => s.length > 0);
    const newSegments = [...segments.slice(0, index), ...redirectSegments];

    // Re-walk siblings, excluding the redirect route itself to prevent loops
    this.loadLazyBoundaries(
      allRoutes.filter(r => r !== route),
      newSegments,
      index,
    );
  }

  /**
   * Loads a lazy module and, once resolved, continues walking into the
   * module's child routes to pick up any deeper lazy boundaries.
   */
  private loadModuleAndContinue(
    result: unknown,
    segments: string[],
    index: number,
  ): void {
    if (!(result instanceof Promise)) {
      return;
    }

    result
      .then((resolved: unknown) => {
        const childRoutes = this.extractRoutes(resolved);
        if (childRoutes && childRoutes.length > 0) {
          this.loadLazyBoundaries(childRoutes, segments, index);
        }
      })
      .catch((err: unknown) => this.handleLoadError(err));
  }

  /** Triggers a bundle download without inspecting the result. */
  private loadBundle(result: unknown): void {
    if (result instanceof Promise) {
      result.then().catch((err: unknown) => this.handleLoadError(err));
    }
  }

  /**
   * Extracts child routes from a `loadChildren` result. Supports both
   * direct `Route[]` returns and NgModule objects with a `routes` property.
   */
  private extractRoutes(resolved: unknown): Route[] | undefined {
    if (Array.isArray(resolved)) {
      return resolved as Route[];
    }

    if (resolved && typeof resolved === 'object') {
      const asModule = resolved as { routes?: unknown };
      if (Array.isArray(asModule.routes)) {
        return asModule.routes as Route[];
      }
    }

    return undefined;
  }

  private handleLoadError(err: unknown): void {
    console.warn('[flPrefetchRoute] bundle load failed', err);
    PrefetchRouteDirective.prefetchedUrls.delete(
      this.router.serializeUrl(
        this.routerLink?.urlTree ?? this.router.parseUrl('/'),
      ),
    );
  }

  private clearTimer(): void {
    if (this.debounceTimer$ !== undefined) {
      clearTimeout(this.debounceTimer$);
      this.debounceTimer$ = undefined;
    }
  }
}
